import { Modal, Button, Form } from "react-bootstrap";
import { useEffect, useState } from "react";
import { fetchCategoriesCached } from "../helpers/categoryApiCache";
import { compareNameUkStrings } from "../helpers/sortUk";

/** Чи є `nodeId` нащадком `ancestorId` (не враховуючи сам `ancestorId` як вузол старту). */
function isStrictDescendantOf(byId, nodeId, ancestorId) {
    if (!ancestorId || String(nodeId) === String(ancestorId)) return false;
    let cur = byId.get(String(nodeId));
    while (cur?.parentId) {
        if (String(cur.parentId) === String(ancestorId)) return true;
        cur = byId.get(String(cur.parentId));
    }
    return false;
}

function sortKeyPath(c, byId) {
    const names = [];
    let cur = c;
    while (cur) {
        names.unshift(String(cur.name ?? ""));
        cur = cur.parentId ? byId.get(String(cur.parentId)) : null;
    }
    return names;
}

function comparePathUk(aPath, bPath) {
    const n = Math.max(aPath.length, bPath.length);
    for (let i = 0; i < n; i += 1) {
        const av = aPath[i] ?? "";
        const bv = bPath[i] ?? "";
        const d = compareNameUkStrings(av, bv);
        if (d !== 0) return d;
    }
    return 0;
}

export default function CategoryEditModal({ show, onClose, onSave, edit, type }) {
    const [name, setName] = useState("");
    const [order, setOrder] = useState(1000);
    const [parentId, setParentId] = useState("");
    const [parentOptions, setParentOptions] = useState([]);

    useEffect(() => {
        if (!show || !type) return undefined;

        let cancelled = false;

        (async () => {
            const list = await fetchCategoriesCached(type, { bypassCache: true });
            if (cancelled || !Array.isArray(list)) return;

            const byId = new Map(list.map((c) => [String(c._id), c]));

            const candidates = list.filter((c) => {
                if (edit && String(c._id) === String(edit._id)) return false;
                if (edit) {
                    if (isStrictDescendantOf(byId, c._id, edit._id)) return false;
                }
                return true;
            });

            const rows = candidates
                .map((row) => ({
                    row,
                    pathLabel: sortKeyPath(row, byId).join(" → "),
                    pathKey: sortKeyPath(row, byId),
                }))
                .sort((a, b) => comparePathUk(a.pathKey, b.pathKey));

            setParentOptions(rows);
        })();

        return () => {
            cancelled = true;
        };
    }, [show, type, edit]);

    useEffect(() => {
        if (edit) {
            setName(edit.name);
            setOrder(edit.order ?? 1000);
            setParentId(edit.parentId ? String(edit.parentId) : "");
        } else {
            setName("");
            setOrder(1000);
            setParentId("");
        }
    }, [edit, show]);

    return (
        <Modal show={show} onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {edit ? "Редагувати категорію" : "Нова категорія"}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form.Group className="mb-2">
                    <Form.Label>Назва</Form.Label>
                    <Form.Control
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </Form.Group>

                <Form.Group className="mb-2">
                    <Form.Label>Батьківська група</Form.Label>
                    <Form.Select
                        value={parentId}
                        onChange={(e) => setParentId(e.target.value)}
                    >
                        <option value="">— коренева категорія —</option>
                        {parentOptions.map(({ row, pathLabel }) => (
                            <option key={row._id} value={row._id}>
                                {pathLabel}
                            </option>
                        ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                        Можна обрати будь-яку існуючу категорію цього типу як батька (шлях у списку
                        для орієнтації). Підкатегорія з&apos;явиться в групі батька у випадаючих списках.
                    </Form.Text>
                </Form.Group>

                <Form.Group>
                    <Form.Label>Порядок</Form.Label>
                    <Form.Control
                        type="number"
                        value={order}
                        onChange={(e) => setOrder(Number(e.target.value))}
                    />
                </Form.Group>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Скасувати
                </Button>
                <Button
                    onClick={() =>
                        onSave({
                            name,
                            order,
                            type,
                            parentId: parentId || null,
                        })
                    }
                    disabled={!name.trim()}
                >
                    Зберегти
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
